import React, { useState } from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import type { MonogramJson } from '@shared/websiteTemplates';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
// eslint-disable-next-line no-restricted-imports -- imports lazy wrapper from admin
import { LazyLoadedMonogramView } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/components/MonogramEditorDialog/MonogramView.lazy';
// eslint-disable-next-line no-restricted-imports -- generated GraphQL query hook only; no admin dependencies
import { useJsonDataContainerByIdQuery } from '@website-custom-components/JsonWrapper.graphql.generated';
import { useTemplatePreviewStore } from '@apps/guest/routes/GuestSite/TemplatePreviewContext';
// 432 = 6 inches × 72 PPI (points per inch, standard screen resolution constant)
export const MONOGRAM_SIZE = 432;

type MonogramViewRendererProps = Readonly<{
  config?: ThemeJson | MonogramJson;
  showPlaceholder?: boolean;
  /** Custom monogram color; falls back to the page font color when absent. */
  fontColor?: string;
  /**
   * A monogram is known to be coming (a jsonContainerId exists) — reserve its box while the
   * JSON fetch + lazy chunk resolve, so the monogram paints into space that already exists
   * instead of shifting the page when it pops in (PRNT header-CLS: 0.63 mobile).
   */
  expectContent?: boolean;
}>;

/**
 * Pure monogram renderer — draws a monogram from already-fetched JSON content.
 * Shared by the standalone `monogram` header layout and the combined
 * photo/video + monogram layouts. The guest site renders the saved (already-personalized)
 * monogram as-is — personalization happens when the design is applied to the event, not here.
 */
export const MonogramViewRenderer = ({ config, showPlaceholder, fontColor, expectContent = false }: MonogramViewRendererProps) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  // PageScaler sizes the monogram imperatively from a ResizeObserver measurement, so the page
  // element resizes to its final geometry after mount. The reserved box below never moves, but
  // Chrome still scores that inner resize (measured 0.279 on mobile). `visibility` keeps it out
  // of CLS entirely — hidden elements are excluded — and the reveal is geometry-neutral, so it
  // scores nothing itself. PageScaler's own `onScale` is the reveal signal: it fires exactly
  // when the child carries final geometry (ResizeObserver guarantees an initial delivery).
  const [isScaled, setIsScaled] = useState(false);
  const handleScale = useEventCallback(() => setIsScaled(true));

  if (!config && !expectContent) {
    return showPlaceholder ? (
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Box
          width={MONOGRAM_SIZE}
          height={MONOGRAM_SIZE}
          backgroundColor="lightgray"
        />
      </Flex>
    ) : null;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      {/* Same geometry as MonogramView's own root (width:100%, max 432px, square) — present from
          SSR onward, so the async content lands inside an already-sized box. */}
      <Box
        width="100%"
        maxWidth={MONOGRAM_SIZE}
        aspectRatio="1/1"
        backgroundColor={!config && showPlaceholder ? 'lightgray' : undefined}
        visibility={config && !isScaled ? 'hidden' : undefined}
      >
        {config && (
          <LazyLoadedMonogramView
            json={config}
            background={pageColor}
            fontColor={fontColor ?? pageFontColor}
            onScale={handleScale}
          />
        )}
      </Box>
    </Flex>
  );
};

type MonogramByContainerIdProps = Readonly<{
  jsonContainerId: string | null | undefined;
  showPlaceholder?: boolean;
  /** Custom monogram color; falls back to the page font color when absent. */
  fontColor?: string;
}>;

/**
 * Fetches the monogram JSON container by id and renders it, so any background
 * layout can drop in a monogram with just a `jsonContainerId`.
 */
export const MonogramByContainerId = ({ jsonContainerId, showPlaceholder = false, fontColor }: MonogramByContainerIdProps) => {
  // Side-by-side template preview: the admin personalizes the inline monogram and ships it in the
  // preview payload, since the previewed design points at the template's unpersonalized container.
  // Outside a preview this is undefined, so the live guest site renders the saved container as usual.
  const previewMonogramContent = useTemplatePreviewStore().getActivePayload()?.previewMonogramContent;

  const { data, loading } = useJsonDataContainerByIdQuery({
    variables: {
      payload: {
        id: jsonContainerId || '',
      },
    },
    batchMode: 'fast',
    // Client-only: the id comes from the upstream header-settings query, so running this
    // server-side forces a separate dependency-chained SSR wave. The monogram is decorative
    // header content that fills in after hydration, so SSR buys nothing here.
    ssr: false,
    skip: !jsonContainerId || !!previewMonogramContent,
  });

  const config = previewMonogramContent ?? data?.jsonDataContainerById?.content;

  return (
    <MonogramViewRenderer
      config={config}
      showPlaceholder={showPlaceholder}
      fontColor={fontColor}
      // Reserve only while the outcome is pending (an ssr:false query reports loading during
      // SSR, so the box is in the server HTML). Once the query settles empty — fetch error,
      // deleted container — collapse like before rather than leaving a permanent blank block;
      // settled WITH content keeps the box via `config`.
      expectContent={loading}
    />
  );
};
